// ═══════════════════════════════════════════════════════
// MOBILE TAB BAR — Bottom navigation for mobile devices
// ═══════════════════════════════════════════════════════
import { useState, useEffect, useRef, useCallback } from "react";
import { type ThemeObject, SP, RADIUS, SHADOW, TYPE, TRANSITION, Z } from "../shared/theme.js";
import { NAV_ICONS, type IconProps } from "../shared/icons.jsx";
import { NAV } from "../shared/navConfig.js";

interface PrimaryTab {
  id: string;
  Icon: (p: IconProps) => JSX.Element;
  label: string;
}

interface MobileTabBarProps {
  active: string;
  setActive: (id: string) => void;
  T: ThemeObject;
}

interface MobileTabBarComponent {
  (props: MobileTabBarProps): JSX.Element;
  HEIGHT: number;
}

const PRIMARY_TABS: PrimaryTab[] = [
  { id: "dashboard", Icon: NAV_ICONS.dashboard, label: "Início" },
  { id: "carteira", Icon: NAV_ICONS.carteira, label: "Carteira" },
  { id: "performance", Icon: NAV_ICONS.performance, label: "Performance" },
  { id: "patrimonio-total", Icon: NAV_ICONS["patrimonio-total"], label: "Patrimônio" },
];

const TAB_BAR_HEIGHT = 64;

const MobileTabBar: MobileTabBarComponent = function MobileTabBar({
  active,
  setActive,
  T,
}: MobileTabBarProps) {
  const [showMore, setShowMore] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMore) return;
    const handler = (e: Event) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target as Node)) {
        setShowMore(false);
      }
    };
    document.addEventListener("touchstart", handler, { passive: true });
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("touchstart", handler);
      document.removeEventListener("mousedown", handler);
    };
  }, [showMore]);

  const handleNav = useCallback(
    (id: string) => {
      setActive(id);
      setShowMore(false);
    },
    [setActive]
  );

  const isPrimary = PRIMARY_TABS.some((t) => t.id === active);

  return (
    <>
      <div style={{ height: TAB_BAR_HEIGHT }} />
      <nav
        role="tablist"
        aria-label="Navegação principal"
        data-tour="mobile-tab-bar"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: TAB_BAR_HEIGHT,
          zIndex: Z.sidebar + 5,
          background: T.card,
          borderTop: `1px solid ${T.border}`,
          boxShadow: "0 -2px 12px rgba(0,0,0,0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-around",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          WebkitBackdropFilter: "blur(12px)",
          backdropFilter: "blur(12px)",
        }}
      >
        {PRIMARY_TABS.map((tab) => {
          const isActive = active === tab.id;
          const TabIcon = tab.Icon;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-label={tab.label}
              onClick={() => handleNav(tab.id)}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                padding: "8px 0",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: isActive ? T.accent : T.muted,
                transition: TRANSITION.fast,
                position: "relative",
              }}
            >
              {isActive && (
                <div
                  style={{
                    position: "absolute",
                    top: 2,
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    background: T.accent,
                  }}
                />
              )}
              <TabIcon size={20} />
              <span
                style={{
                  fontSize: "0.625rem",
                  fontWeight: isActive ? TYPE.semibold : TYPE.regular,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {tab.label}
              </span>
            </button>
          );
        })}

        <button
          role="tab"
          aria-selected={!isPrimary}
          aria-label="Mais opções"
          aria-expanded={showMore}
          onClick={() => setShowMore((v) => !v)}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            padding: "8px 0",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: !isPrimary || showMore ? T.accent : T.muted,
            transition: TRANSITION.fast,
          }}
        >
          <svg
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="currentColor"
            stroke="none"
            aria-hidden="true"
          >
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
          </svg>
          <span
            style={{
              fontSize: "0.625rem",
              fontWeight: !isPrimary || showMore ? TYPE.semibold : TYPE.regular,
              lineHeight: 1,
            }}
          >
            Mais
          </span>
        </button>
      </nav>

      {showMore && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: Z.overlay,
            background: "rgba(0,0,0,0.55)",
            animation: "lpa-fade-in 0.2s ease",
          }}
        >
          <div
            ref={overlayRef}
            style={{
              position: "absolute",
              bottom: TAB_BAR_HEIGHT,
              left: 0,
              right: 0,
              maxHeight: "70vh",
              overflowY: "auto",
              background: T.card,
              borderTop: `1px solid ${T.border}`,
              borderRadius: `${RADIUS.lg} ${RADIUS.lg} 0 0`,
              boxShadow: SHADOW.lg,
              padding: `${SP[4]} ${SP[4]} ${SP[6]}`,
              animation: "lpa-scale-in 0.2s ease",
            }}
          >
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: T.muted,
                opacity: 0.4,
                margin: `0 auto ${SP[4]}`,
              }}
            />

            {NAV.map((item: any) => {
              if (item.isGroup) {
                return (
                  <div key={item.id} style={{ marginBottom: SP[4] }}>
                    <div
                      style={{
                        fontSize: TYPE.xs,
                        fontWeight: TYPE.semibold,
                        color: T.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: SP[2],
                        paddingLeft: SP[2],
                      }}
                    >
                      {item.label}
                    </div>
                    <div
                      style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: SP[2] }}
                    >
                      {item.children
                        .filter((c: any) => !c.hidden)
                        .map((child: any) => {
                          const isChildActive = active === child.id;
                          return (
                            <button
                              key={child.id}
                              onClick={() => handleNav(child.id)}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 4,
                                padding: SP[3],
                                background: isChildActive ? `${T.accent}18` : T.surface,
                                border: isChildActive
                                  ? `1.5px solid ${T.accent}`
                                  : `1px solid ${T.border}`,
                                borderRadius: RADIUS.md,
                                cursor: "pointer",
                                color: isChildActive ? T.accent : T.text,
                                transition: TRANSITION.fast,
                              }}
                            >
                              <child.Icon size={20} />
                              <span
                                style={{
                                  fontSize: TYPE.xs,
                                  fontWeight: isChildActive ? TYPE.semibold : TYPE.regular,
                                  textAlign: "center",
                                  lineHeight: 1.2,
                                }}
                              >
                                {child.label}
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                );
              }

              if (PRIMARY_TABS.some((t) => t.id === item.id)) return null;
              const isItemActive = active === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: SP[3],
                    width: "100%",
                    padding: SP[3],
                    marginBottom: SP[2],
                    background: isItemActive ? `${T.accent}18` : "transparent",
                    border: "none",
                    borderRadius: RADIUS.md,
                    cursor: "pointer",
                    color: isItemActive ? T.accent : T.text,
                    transition: TRANSITION.fast,
                  }}
                >
                  <item.Icon size={18} />
                  <span
                    style={{
                      fontSize: TYPE.md,
                      fontWeight: isItemActive ? TYPE.semibold : TYPE.regular,
                    }}
                  >
                    {item.label}
                  </span>
                </button>
              );
            })}

            <div
              style={{ borderTop: `1px solid ${T.border}`, marginTop: SP[2], paddingTop: SP[3] }}
            >
              <button
                onClick={() => handleNav("perfil")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: SP[3],
                  width: "100%",
                  padding: SP[3],
                  background: active === "perfil" ? `${T.accent}18` : "transparent",
                  border: "none",
                  borderRadius: RADIUS.md,
                  cursor: "pointer",
                  color: active === "perfil" ? T.accent : T.text,
                  transition: TRANSITION.fast,
                }}
              >
                <NAV_ICONS.perfil size={18} />
                <span
                  style={{
                    fontSize: TYPE.md,
                    fontWeight: active === "perfil" ? TYPE.semibold : TYPE.regular,
                  }}
                >
                  Meu Perfil
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
} as MobileTabBarComponent;

MobileTabBar.HEIGHT = TAB_BAR_HEIGHT;

export default MobileTabBar;
